import type { Strain, StrainSummary } from '@shared';
import {
  getLocalCatalogueImport,
  type LocalCatalogueImportProvenance,
} from './provenance';

export type BootstrapAvailability =
  | 'available'
  | 'already_imported'
  | 'conflict';

export interface BootstrapIdentityMatch {
  status: BootstrapAvailability;
  /** Local strain id when already imported or conflict. */
  localStrainId?: string;
  reason: string;
}

/**
 * Classify a server source product against the device-local catalogue.
 *
 * Identity rules (strongest first):
 * 1. Source has productId → exact provenance.sourceProductId proves prior import
 * 2. Source has no productId → sourceServerStrainId proves prior import
 * 3. Potential conflict → productId matches a local row without matching
 *    bootstrap provenance (never brand/productName alone)
 * 4. Otherwise available
 */
export function classifyBootstrapSource(
  source: Pick<StrainSummary, 'id' | 'productId' | 'name' | 'productName' | 'brand'>,
  localStrains: Strain[],
): BootstrapIdentityMatch {
  if (source.productId != null && source.productId !== '') {
    for (const local of localStrains) {
      const provenance = getLocalCatalogueImport(local);
      if (provenance?.sourceProductId === source.productId) {
        return {
          status: 'already_imported',
          localStrainId: local.id,
          reason:
            'This server product was already imported into this device catalogue.',
        };
      }
    }

    for (const local of localStrains) {
      if (local.productId !== source.productId) continue;
      // Same spreadsheet Product ID exists locally without proven bootstrap link.
      return {
        status: 'conflict',
        localStrainId: local.id,
        reason:
          'A local product shares this Product ID but is not proven to be the same prior import.',
      };
    }
  } else {
    for (const local of localStrains) {
      const provenance = getLocalCatalogueImport(local);
      if (provenance?.sourceServerStrainId === source.id) {
        return {
          status: 'already_imported',
          localStrainId: local.id,
          reason:
            'This server product was already imported into this device catalogue.',
        };
      }
    }
  }

  return {
    status: 'available',
    reason: 'Not previously imported into this device catalogue.',
  };
}

export function findLocalBySourceServerId(
  localStrains: Strain[],
  sourceServerStrainId: string,
): { strain: Strain; provenance: LocalCatalogueImportProvenance } | null {
  for (const local of localStrains) {
    const provenance = getLocalCatalogueImport(local);
    if (provenance?.sourceServerStrainId === sourceServerStrainId) {
      return { strain: local, provenance };
    }
  }
  return null;
}
